import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClockCounterClockwise, Check, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { picksLabel } from "@/lib/funnelOptions";
import { inboxChanged } from "@/components/layout/useInboxCount";

export const FIELD_LABELS = {
  firm_name: "Company name",
  phone: "Phone",
  brand_color: "Brand color",
  logo: "Logo",
  tcpa_language: "Consent text",
  funnel_picks: "Funnels",
};

export const STATUS_STYLE = {
  applied: "border-emerald-800 bg-emerald-100 text-emerald-900",
  approved: "border-emerald-800 bg-emerald-100 text-emerald-900",
  pending: "border-amber-800 bg-amber-100 text-amber-900",
  rejected: "border-rose-800 bg-rose-100 text-rose-900",
  superseded: "border-zinc-700 bg-zinc-100 text-zinc-700",
};

export function shortValue(field, v) {
  if (field === "funnel_picks") return picksLabel(v);
  if (v == null || v === "") return field === "tcpa_language" ? "Standard wording" : "—";
  if (field === "logo") return "Image";
  const s = String(v);
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
}

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function PendingConsentCard({ change, busy, onReview }) {
  return (
    <div className="border-2 border-amber-600 bg-amber-50 p-3" data-testid={`profile-change-pending-${change.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold">{change.firm_name}</span>
        <span className="font-mono text-[10px] text-zinc-600">{change.page_slug}</span>
        <span className="text-[10px] text-zinc-600 ml-auto">
          Sent by {change.submitted_by?.name || change.submitted_by?.email} · {fmt(change.submitted_at)}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        <div>
          <div className="label-mono mb-1">Live now</div>
          <div className="border border-black bg-white p-2 text-[11px] leading-relaxed whitespace-pre-wrap">
            {change.field === "tcpa_language" ? (change.before || "Standard wording with the firm's name") : shortValue(change.field, change.before)}
          </div>
        </div>
        <div>
          <div className="label-mono mb-1">Proposed</div>
          <div className="border-2 border-black bg-white p-2 text-[11px] leading-relaxed whitespace-pre-wrap"
               data-testid={`profile-change-proposed-${change.id}`}>
            {change.field === "tcpa_language" ? change.after : shortValue(change.field, change.after)}          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" disabled={busy} onClick={() => onReview(change, "approve")}
                data-testid={`profile-change-approve-${change.id}`}
                className="border-2 border-emerald-800 bg-emerald-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1">
          <Check size={11} weight="bold" /> Approve & publish
        </button>
        <button type="button" disabled={busy} onClick={() => onReview(change, "reject")}
                data-testid={`profile-change-reject-${change.id}`}
                className="border-2 border-rose-800 bg-white text-rose-800 px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40 hover:bg-rose-800 hover:text-white inline-flex items-center gap-1">
          <X size={11} weight="bold" /> Reject
        </button>
      </div>
    </div>
  );
}

export function ChangeHistoryTable({ changes, testId, showFirm = true }) {
  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-widest";
  return (
    <div className="overflow-x-auto border border-black bg-white">
      <table className="w-full text-xs min-w-[560px]" data-testid={testId}>
        <thead className="bg-zinc-100 border-b border-black">
          <tr>
            <th className={th}>When</th>{showFirm && <th className={th}>Buyer</th>}
            <th className={th}>Field</th><th className={th}>Change</th><th className={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={c.id} className="border-b border-zinc-200 align-top" data-testid={`profile-change-row-${c.id}`}>
              <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap">{fmt(c.submitted_at)}</td>
              {showFirm && <td className="px-3 py-2">{c.firm_name}<div className="text-[10px] text-zinc-500">{c.submitted_by?.email}</div></td>}
              <td className="px-3 py-2 whitespace-nowrap">{FIELD_LABELS[c.field] || c.field}</td>
              <td className="px-3 py-2">
                <span className="text-zinc-500">{shortValue(c.field, c.before)}</span> → <b>{shortValue(c.field, c.after)}</b>
                {c.review_note && <div className="text-[10px] text-zinc-600 mt-1">Note: {c.review_note}</div>}
              </td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${STATUS_STYLE[c.status] || ""}`}>
                  {c.status === "applied" ? "live" : c.status}
                </span>
              </td>
            </tr>
          ))}
          {!changes.length && (
            <tr><td colSpan={showFirm ? 5 : 4} className="px-3 py-5 text-center label-mono">No changes yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const BuyerProfileChanges = ({ onReviewed }) => {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [p, h] = await Promise.all([
      api.get("/admin/buyer-profile-changes", { params: { status: "pending" } }),
      api.get("/admin/buyer-profile-changes", { params: { limit: 50 } }),
    ]);
    setPending(p.data?.changes || []);
    setHistory(h.data?.changes || []);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const review = async (change, action) => {
    const note = action === "reject" ? window.prompt("Reason for rejecting (the buyer will see this):", "") : "";
    if (note === null) return;
    setBusy(change.id);
    try {
      await api.post(`/admin/buyer-profile-changes/${change.id}/${action}`, { note: note || null });
      const what = change.field === "funnel_picks" ? "Funnel change" : "Consent text";
      toast.success(action === "approve" ? `${what} approved — it's live now` : "Change rejected");
      inboxChanged();
      await load();
      onReviewed?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the review");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border-t border-black p-4" data-testid="buyer-profile-changes">
      <div className="flex items-center gap-2 mb-2">
        <ClockCounterClockwise size={14} weight="bold" />
        <div className="label-mono">Buyer profile changes</div>
        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-800 bg-amber-100 text-amber-900"
              data-testid="buyer-profile-pending-count">
          {pending.length} waiting for approval
        </span>
      </div>
      <div className="text-[10.5px] text-zinc-600 mb-3">
        Buyers&apos; name, phone, brand color and logo changes go live right away. New consent text — and funnel changes from buyers set to need approval — wait here until you approve them.
      </div>
      <div className="flex flex-col gap-3 mb-4">
        {pending.map((c) => <PendingConsentCard key={c.id} change={c} busy={busy === c.id} onReview={review} />)}
      </div>
      <ChangeHistoryTable changes={history} testId="buyer-profile-history" />
    </div>
  );
};
